import { ref } from 'vue';
import WacallsCallAPI from 'dashboard/api/wacallsCall';

// MODULE-SCOPE singleton — Button (startCall/answerInbound) and Panel
// (toggleMute / hangup) must share ONE AudioContext + RTCPeerConnection +
// MediaStream.
const SAMPLE_RATE = 16000;
const CAPTURE_WORKLET_URL = '/worklets/capture-processor.js';
// ?v= força o navegador a buscar o worklet novo (arquivo estático em cache).
const PLAYBACK_WORKLET_URL = '/worklets/playback-processor.js?v=2';

let pc = null;
let micStream = null;
let audioCtx = null;
let remoteAudioEl = null;
let currentCallId = null;
const isMuted = ref(false);

// ---------------------------------------------------------------------------
// PCM helpers
// ---------------------------------------------------------------------------
const float32ToInt16LE = pcm => {
  const view = new DataView(new ArrayBuffer(pcm.length * 2));
  for (let i = 0; i < pcm.length; i += 1) {
    let s = pcm[i];
    if (Number.isNaN(s)) s = 0;
    else if (s > 1) s = 1;
    else if (s < -1) s = -1;
    view.setInt16(
      i * 2,
      s < 0 ? Math.round(s * 32768) : Math.round(s * 32767),
      true
    );
  }
  return view.buffer;
};

const int16LEToFloat32 = buf => {
  const view = new DataView(buf);
  const n = Math.floor(buf.byteLength / 2);
  const out = new Float32Array(n);
  for (let i = 0; i < n; i += 1) out[i] = view.getInt16(i * 2, true) / 32768;
  return out;
};

// ---------------------------------------------------------------------------
// Remote audio element (hidden, appended to body for the call lifetime)
// ---------------------------------------------------------------------------
const ensureRemoteAudio = () => {
  if (remoteAudioEl) return remoteAudioEl;
  remoteAudioEl = document.createElement('audio');
  remoteAudioEl.autoplay = true;
  remoteAudioEl.setAttribute('playsinline', '');
  remoteAudioEl.style.display = 'none';
  document.body.appendChild(remoteAudioEl);
  return remoteAudioEl;
};

const teardownRemoteAudio = () => {
  if (!remoteAudioEl) return;
  remoteAudioEl.pause();
  remoteAudioEl.srcObject = null;
  remoteAudioEl.remove();
  remoteAudioEl = null;
};

// ---------------------------------------------------------------------------
// ICE gathering — non-trickle: wait for 'complete' or 3 s timeout
// ---------------------------------------------------------------------------
const waitForIceGathering = localPc =>
  new Promise(resolve => {
    if (localPc.iceGatheringState === 'complete') {
      resolve();
      return;
    }
    const timer = setTimeout(resolve, 3000);
    const check = () => {
      if (localPc.iceGatheringState === 'complete') {
        clearTimeout(timer);
        localPc.removeEventListener('icegatheringstatechange', check);
        resolve();
      }
    };
    localPc.addEventListener('icegatheringstatechange', check);
  });

// ---------------------------------------------------------------------------
// Shared WebRTC bridge setup (used by both outbound startCall and inbound
// answerInbound). Assumes currentCallId is already set by the caller.
// ---------------------------------------------------------------------------
// Captura o microfone ANTES de discar: sem mic não existe ligação — discar
// primeiro deixava o cliente atendendo uma chamada MUDA quando o mic falhava.
const acquireMic = async () => {
  micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
};

const establishBridge = async (conversationId, callId) => {
  // 1. Mic access (já garantido no preflight; revalida por segurança)
  if (!micStream || !micStream.active) await acquireMic();

  // 2. RTCPeerConnection (DataChannel only — audio travels as raw PCM)
  pc = new RTCPeerConnection({ iceServers: [] });

  // ICE diagnostics
  pc.addEventListener('iceconnectionstatechange', () => {
    // eslint-disable-next-line no-console
    console.info('[wacall] iceConnectionState', pc?.iceConnectionState);
  });

  // 3. DataChannel for PCM — sem ordem e sem reenvio, como RTP: pacote perdido
  // vira um buraco de 8 ms em vez de travar a fila até o reenvio (o canal
  // ordenado e confiável picotava a ligação a cada perda na rede).
  const dc = pc.createDataChannel('pcm', { ordered: false, maxRetransmits: 0 });
  dc.binaryType = 'arraybuffer';

  // 4. AudioContext + worklets
  audioCtx = new AudioContext({ sampleRate: SAMPLE_RATE });
  await audioCtx.audioWorklet.addModule(CAPTURE_WORKLET_URL);
  await audioCtx.audioWorklet.addModule(PLAYBACK_WORKLET_URL);
  await audioCtx.resume();

  // 5. Mic → capture worklet → DataChannel
  const micSource = audioCtx.createMediaStreamSource(micStream);
  const captureNode = new AudioWorkletNode(audioCtx, 'capture-processor');
  captureNode.port.onmessage = e => {
    if (dc.readyState === 'open') {
      dc.send(float32ToInt16LE(e.data));
    }
  };
  micSource.connect(captureNode);
  captureNode.connect(audioCtx.destination);

  // 6. DataChannel → playback worklet → hidden audio element
  const playbackNode = new AudioWorkletNode(audioCtx, 'playback-processor');
  const streamDest = audioCtx.createMediaStreamDestination();
  playbackNode.connect(streamDest);
  dc.onmessage = e => playbackNode.port.postMessage(int16LEToFloat32(e.data));

  // 7. SDP offer + ICE gathering
  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);
  await waitForIceGathering(pc);

  // 8. Exchange SDP with backend
  const { data: rtcData } = await WacallsCallAPI.webrtc(conversationId, {
    call_id: callId,
    sdp_offer: pc.localDescription.sdp,
  });
  await pc.setRemoteDescription({
    type: 'answer',
    sdp: rtcData.sdp_answer,
  });

  // 9. Play remote audio via hidden <audio> element
  const audioEl = ensureRemoteAudio();
  audioEl.srcObject = streamDest.stream;
  audioEl.play().catch(() => {});
};

// ---------------------------------------------------------------------------
// Module-level teardown — also called by the actionCable terminated handler
// so the mic/AudioContext/PeerConnection are released when the callee hangs up
// without the agent clicking the in-panel hang-up button.
// ---------------------------------------------------------------------------
export const forceCleanup = () => {
  micStream?.getTracks().forEach(t => t.stop());
  audioCtx?.close().catch(() => {});
  pc?.close();
  teardownRemoteAudio();
  pc = null;
  micStream = null;
  audioCtx = null;
  currentCallId = null;
  isMuted.value = false;
};

// ---------------------------------------------------------------------------
// Exported composable
// ---------------------------------------------------------------------------
export function useWacallsCall() {
  const cleanup = forceCleanup;

  // Outbound call: start via backend then establish WebRTC bridge.
  const startCall = async conversationId => {
    // Re-entrancy guard
    if (pc) cleanup();

    // 0. Microfone PRIMEIRO: se falhar, nada é discado.
    try {
      await acquireMic();
    } catch (e) {
      const err = new Error('mic_denied');
      err.code = 'mic_denied';
      throw err;
    }

    // 1. Initiate call on backend → get call_id
    const { data } = await WacallsCallAPI.start(conversationId);
    const callId = data.call_id;
    currentCallId = callId;

    await establishBridge(conversationId, callId);

    return { callId };
  };

  // Inbound call: accept via backend then establish the same WebRTC bridge.
  const answerInbound = async (conversationId, callId) => {
    // Re-entrancy guard
    if (pc) cleanup();

    // Microfone antes de aceitar — mesma razão do outbound.
    try {
      await acquireMic();
    } catch (e) {
      const err = new Error('mic_denied');
      err.code = 'mic_denied';
      throw err;
    }

    await WacallsCallAPI.accept(conversationId, { call_id: callId });
    currentCallId = callId;

    await establishBridge(conversationId, callId);

    return { callId };
  };

  const toggleMute = () => {
    isMuted.value = !isMuted.value;
    if (micStream) {
      micStream.getAudioTracks().forEach(t => {
        t.enabled = !isMuted.value;
      });
    }
    return isMuted;
  };

  const hangup = async conversationId => {
    try {
      if (currentCallId) {
        await WacallsCallAPI.terminate(conversationId, {
          call_id: currentCallId,
        });
      }
    } finally {
      cleanup();
    }
  };

  return {
    startCall,
    answerInbound,
    toggleMute,
    hangup,
    isMuted,
  };
}

// Export PCM helpers for testing
export { float32ToInt16LE, int16LEToFloat32 };
